import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ContactSection from '../components/ContactSection';
import { Award, CheckCircle2, Camera, MapPin, Star, Shield } from 'lucide-react';
import { breadcrumbSchema } from '../utils/structuredData';

const MatterportServiceProviderPage = () => {
  const equipment = [
    {
      name: 'Matterport Pro3',
      description: 'The most advanced Matterport camera, offering the highest resolution, fastest capture speed and best low-light performance. Ideal for commercial properties, large venues and projects requiring the highest quality output.'
    },
    {
      name: 'Matterport Pro2',
      description: 'The industry-standard Matterport camera used worldwide. Reliable, efficient and proven across thousands of scanning projects. Well suited to residential properties, offices and standard commercial spaces.'
    },
    {
      name: 'Leica BLK360',
      description: 'A survey-grade laser scanner compatible with the Matterport platform. Used for projects requiring higher geometric accuracy, larger spaces or integration with point cloud and BIM workflows.'
    },
    {
      name: 'Leica RTC360',
      description: 'Our flagship laser scanner for large-scale and complex projects. Captures millions of points per second with survey-grade accuracy, enabling combined Matterport and point cloud deliverables from a single site visit.'
    }
  ];

  const coverageAreas = [
    'London (all boroughs)',
    'South East England',
    'South West England',
    'Midlands',
    'North of England',
    'Scotland',
    'Wales',
    'Northern Ireland',
    'Republic of Ireland',
    'Europe (by arrangement)'
  ];

  const differentiators = [
    {
      icon: Award,
      title: 'Certified Matterport Partner',
      description: 'We are an official Matterport Service Provider, certified by Matterport to deliver professional scanning services to the highest standards.'
    },
    {
      icon: Camera,
      title: 'Latest Equipment',
      description: 'We invest in the newest Matterport cameras and laser scanners, ensuring every project benefits from the best available technology.'
    },
    {
      icon: MapPin,
      title: 'Nationwide Coverage',
      description: 'Based in London with teams available across the UK. We cover projects of any size, anywhere in the country, with consistent quality.'
    },
    {
      icon: Star,
      title: 'Multi-Discipline Expertise',
      description: 'Beyond Matterport tours, we offer point cloud surveys, BIM modelling, drone surveys and CGI — a complete 3D scanning service.'
    },
    {
      icon: Shield,
      title: 'Insured and Compliant',
      description: 'Full professional indemnity and public liability insurance. CSCS-certified operatives for construction site access. DBS-checked staff for healthcare and education projects.'
    }
  ];

  const portfolioHighlights = [
    'NHS hospitals and neonatal units across multiple trusts',
    'Major commercial office buildings in the City of London and Canary Wharf',
    'Heritage properties including listed buildings and conservation areas',
    'Hotel and leisure venues across the UK and Europe',
    'Construction sites for Tier 1 contractors',
    'University campuses and educational facilities',
    'Retail portfolios for national landlords and agents'
  ];

  const faqs = [
    {
      question: 'What does it mean to be an official Matterport Service Provider?',
      answer: 'An official Matterport Service Provider (MSP) is a company that has been vetted and certified by Matterport to deliver professional 3D scanning services. MSPs meet Matterport\'s quality standards, use approved equipment, and have demonstrated expertise through a portfolio of completed projects. Choosing an MSP gives you confidence that your project will be handled by experienced professionals with direct access to Matterport\'s technical support and resources.'
    },
    {
      question: 'Do you cover areas outside London?',
      answer: 'Yes. While we are based in London, we provide Matterport scanning services across the entire UK including Scotland, Wales and Northern Ireland. We also undertake projects in the Republic of Ireland and mainland Europe by arrangement. For projects outside London, we can often combine multiple sites in a single trip to keep costs efficient.'
    },
    {
      question: 'Can you handle large-scale portfolio scanning projects?',
      answer: 'Absolutely. We have experience managing multi-site scanning programmes for national landlords, NHS trusts, hotel groups and retail portfolios. We assign a dedicated project manager, establish standardised capture protocols, and deliver consistent outputs across all sites. We can scale our teams to meet demanding timelines.'
    },
    {
      question: 'What additional services do you offer beyond Matterport scanning?',
      answer: 'In addition to Matterport 3D tours, we offer point cloud laser scanning, Scan to BIM Revit modelling, drone surveys and aerial photography, CGI visualisation and virtual staging, timelapse cameras, and 2D CAD drawing production. This means we can handle complex projects that require multiple deliverables from a single provider.'
    },
    {
      question: 'How quickly can you deliver a completed virtual tour?',
      answer: 'For standard Matterport tours, we typically deliver within 24-48 hours of the site scan. Larger or more complex projects may take 3-5 working days. For urgent requirements, we offer same-day and next-day turnaround on standard scans. Processing times for additional deliverables such as floor plans or point clouds vary by project.'
    },
    {
      question: 'How much does it cost to hire a Matterport Service Provider?',
      answer: 'Our Matterport scanning services start from £295 for smaller spaces. Pricing depends on the size of the property, number of scan positions required, and any additional deliverables. We offer competitive rates for multi-property and ongoing scanning contracts. Visit our pricing page or contact us for a detailed quote tailored to your specific requirements.'
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Matterport Service Provider",
    "name": "Official Matterport Service Provider UK",
    "provider": {
      "@type": "Organization",
      "name": "Digital Scanning Group",
      "url": "https://www.digitalscanninggroup.com"
    },
    "areaServed": [
      { "@type": "Place", "name": "United Kingdom" },
      { "@type": "Place", "name": "Europe" }
    ],
    "description": "Digital Scanning Group is an official Matterport Service Provider covering the entire UK. Professional 3D scanning with Pro3, Pro2 and Leica equipment."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Official Matterport Service Provider UK | Certified 3D Scanning Partner"
        description="Digital Scanning Group is an official Matterport Service Provider covering the UK. Pro3 and Pro2 cameras. Nationwide coverage."
        keywords="matterport service provider uk, matterport partner uk, certified matterport scanning, official matterport provider, matterport pro3, matterport scanning company"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/matterport-service-provider-uk` : ''}
        structuredData={[
          serviceSchema,
          faqSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Matterport Service Provider UK', url: typeof window !== 'undefined' ? `${window.location.origin}/matterport-service-provider-uk` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <Award className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">CERTIFIED MATTERPORT PARTNER</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Official Matterport Service Provider UK
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Digital Scanning Group is an official Matterport Service Provider, certified to deliver professional 3D scanning services across the United Kingdom and Europe. We combine the latest Matterport technology with deep industry expertise to create immersive virtual experiences for every sector.
            </p>
            <div className="mt-8">
              <Link
                to="/pricing"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBzY2FubmluZyUyMHRlYW18ZW58MHx8fHw&ixlib=rb-4.1.0&q=85&w=1400"
              alt="Digital Scanning Group - Official Matterport Service Provider"
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* About DSG as MSP */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Your Certified Matterport Partner
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  As an official Matterport Service Provider, Digital Scanning Group has been vetted and certified by Matterport to deliver professional 3D scanning services to the highest standards. This certification is not simply a label — it represents a proven track record of quality, reliability and technical competence verified by Matterport themselves.
                </p>
                <p>
                  We have completed over 500 scanning projects across the UK and Europe, serving clients in construction, healthcare, property marketing, leisure, education and heritage sectors. Our team includes CSCS-certified operatives for construction site access and DBS-checked staff for sensitive environments such as hospitals and schools.
                </p>
                <p>
                  What sets us apart from other Matterport providers is the breadth of our capabilities. While many scanning companies offer only basic virtual tours, we provide a full suite of 3D services — from Matterport tours and point cloud surveys to BIM modelling, drone surveys and CGI visualisation. This means we can handle complex, multi-deliverable projects through a single provider, saving you time and ensuring consistency across all outputs.
                </p>
                <p>
                  We invest continuously in the latest equipment, maintaining a fleet that includes the Matterport Pro3, Pro2, Leica BLK360 and Leica RTC360. This ensures we always have the right tool for every project, whether it is a compact apartment or a sprawling industrial complex. If you are unsure which technology suits your needs, our guide on <Link to="/blog/matterport-vs-laser-scanning-which-do-you-need" className="text-blue-600 hover:underline">Matterport vs laser scanning</Link> explains the key differences.
                </p>
                <p>
                  Our work spans a wide range of sectors. We have delivered scanning projects for <Link to="/blog/3d-scanning-nhs-hospitals" className="text-blue-600 hover:underline">NHS hospitals</Link>, commercial office buildings, heritage properties and construction sites across the UK. For architects, we have written about how <Link to="/blog/matterport-for-architects" className="text-blue-600 hover:underline">Matterport supports architectural workflows</Link> from feasibility through to as-built documentation. For property agents marketing <Link to="/commercial-property-3d-tours-london" className="text-blue-600 hover:underline">commercial properties</Link>, our tours deliver measurable improvements in enquiry quality and viewing efficiency.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Coverage Areas</h3>
              <div className="space-y-4">
                {coverageAreas.map((area, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <MapPin className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{area}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="mt-8 w-full inline-block text-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Equipment */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Our Equipment</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              We maintain a fleet of the latest Matterport cameras and laser scanners, ensuring the right tool for every project.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {equipment.map((item, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <div className="inline-flex p-3 bg-blue-100 rounded-xl mb-4">
                    <Camera className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What Sets Us Apart */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">What Sets Digital Scanning Group Apart</h2>
            <div className="space-y-6">
              {differentiators.map((diff, index) => {
                const Icon = diff.icon;
                return (
                  <div key={index} className="flex items-start gap-6 p-6 bg-blue-50 rounded-2xl">
                    <div className="inline-flex p-4 bg-blue-100 rounded-xl flex-shrink-0">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{diff.title}</h3>
                      <p className="text-gray-600">{diff.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Portfolio Highlights */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Portfolio Highlights</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              A selection of the sectors and project types we have delivered for clients across the UK.
            </p>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {portfolioHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Related Services */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Our Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/matterport-virtual-tours-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Camera className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Matterport Virtual Tours</h3>
                <p className="text-gray-600 text-sm">Immersive 3D virtual tours for any property or venue.</p>
              </Link>
              <Link to="/point-cloud-surveys-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Star className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Point Cloud Surveys</h3>
                <p className="text-gray-600 text-sm">Survey-grade laser scanning for accurate spatial data.</p>
              </Link>
              <Link to="/scan-to-bim-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Shield className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Scan to BIM</h3>
                <p className="text-gray-600 text-sm">Revit modelling from scan data for design and construction.</p>
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Work With an Official Matterport Partner</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact us today to discuss your project. Whether you need a single scan or a nationwide programme, we have the equipment, expertise and capacity to deliver.
            </p>
            <Link
              to="/pricing"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default MatterportServiceProviderPage;
